import { ArrowRight, ShieldCheck, Activity, Sparkles } from "lucide-react";

const BOOK_URL = "https://kneadrelief.noterro.com/";

export default function Hero() {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative pt-64 pb-20 sm:pt-72 sm:pb-28 lg:pt-[320px] lg:pb-32 overflow-hidden"
    >
      {/* Decorative background */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-50/60 via-white to-white" />
        <div className="absolute -top-32 -right-40 w-[640px] h-[640px] rounded-full bg-blue-200/30 blur-[120px]" />
        <div className="absolute top-40 -left-40 w-[480px] h-[480px] rounded-full bg-sky-200/30 blur-[100px]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Centered logo above the hero */}
        <div className="flex justify-center mb-10 lg:mb-14 kr-fade-up">
          <img
            src="https://customer-assets-cm19k8pv.emergentagent.net/job_knead-refresh/artifacts/tftgtah7_KneadRelief_logo_transparent_highres.webp"
            alt="KneadRelief — Pain Relief, Movement & Recovery in Calgary"
            data-testid="hero-logo"
            className="object-contain h-32 sm:h-40 lg:h-56 w-auto"
            style={{ maxWidth: "900px" }}
          />
        </div>

        <div className="grid grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="col-span-12 lg:col-span-7 kr-fade-up">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-blue-800 text-xs font-semibold uppercase tracking-[0.18em] mb-6">
              <Sparkles size={14} strokeWidth={2} />
              Calgary's Premier Pain & Injury Clinic
            </div>

            <h1
              data-testid="hero-heading"
              className="font-display text-4xl sm:text-5xl lg:text-[64px] font-bold leading-[1.05] tracking-tight text-slate-900"
            >
              Pain and injury relief with{" "}
              <span className="relative inline-block">
                <span className="relative z-10 text-blue-800">RAPID NeuroFascial</span>
                <span className="absolute left-0 right-0 bottom-1 h-3 bg-blue-200/60 -z-0 rounded-sm" />
              </span>{" "}
              technique.
            </h1>

            <p className="mt-6 text-lg sm:text-xl text-slate-600 leading-relaxed max-w-xl">
              Imagine a world where your body isn't a source of pain, but a source of joy.
              Expert therapy that searches for the source — not just the symptoms.
            </p>

            <div className="mt-9 flex flex-col sm:flex-row gap-3 sm:gap-4">
              <a
                href={BOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-book-now-button"
                className="inline-flex items-center justify-center gap-2 bg-blue-800 text-white hover:bg-blue-900 rounded-full px-7 py-4 font-semibold shadow-lg shadow-blue-900/25 hover:-translate-y-0.5 transition-all duration-300"
              >
                Book Your Appointment
                <ArrowRight size={18} strokeWidth={2.2} />
              </a>
              <a
                href="#rapid"
                data-testid="hero-learn-more"
                className="inline-flex items-center justify-center gap-2 bg-white text-blue-900 border border-slate-200 hover:border-blue-300 hover:bg-blue-50/60 rounded-full px-7 py-4 font-semibold transition-all"
              >
                What is RAPID?
              </a>
            </div>

            {/* Trust strip */}
            <div className="mt-10 grid grid-cols-3 gap-4 sm:gap-6 max-w-lg">
              {[
                { icon: ShieldCheck, label: "Direct billing", sub: "Insurance friendly" },
                { icon: Activity, label: "RAPID NFR", sub: "Elite specialist" },
                { icon: Sparkles, label: "Personalised", sub: "Care first" },
              ].map((t) => (
                <div key={t.label} className="flex flex-col">
                  <t.icon size={20} strokeWidth={1.6} className="text-blue-800 mb-2" />
                  <div className="text-sm font-semibold text-slate-900">{t.label}</div>
                  <div className="text-xs text-slate-500">{t.sub}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Image side */}
          <div className="col-span-12 lg:col-span-5 relative kr-fade-up" style={{ animationDelay: "120ms" }}>
            <div className="relative">
              <div className="absolute -inset-6 bg-gradient-to-br from-blue-100 to-amber-50 rounded-[40px] -rotate-2" />
              <div className="relative rounded-[36px] overflow-hidden shadow-2xl shadow-blue-900/15 ring-1 ring-slate-900/5">
                <img
                  src="https://static.wixstatic.com/media/6616d1_9c54961a9d4b415fae1cf64fb1aecfd0~mv2.jpg/v1/fill/w_900,h_1100,al_c,q_85,enc_auto/dennis-lee-rapid-nfr-session.jpg"
                  alt="Dennis Lee delivering a RAPID NeuroFascial Reset session in Calgary"
                  className="w-full h-[420px] sm:h-[520px] object-cover"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-950/40 via-transparent to-transparent" />
              </div>

              {/* Floating card */}
              <div className="absolute -bottom-6 -left-4 sm:-left-8 bg-white rounded-2xl p-4 shadow-xl shadow-blue-900/10 border border-slate-100 max-w-[240px]">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center">
                    <Activity size={20} strokeWidth={2} className="text-blue-800" />
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Specialist</div>
                    <div className="text-sm font-bold text-slate-900">RAPID NFR Specialist</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
